import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@truefit/http-utils';
import { useSnackbar } from 'notistack';

import type { GameCamera } from './useGameCamerasQuery';
import { getGameCamerasQueryKey } from './useGameCamerasQuery';

export interface CreateGameCamerasFromSetupData {
  cameraSetupId: number;
}

export interface CreateSingleGameCameraData {
  cameraId: number;
  positionX: number;
  positionY: number;
  positionZ: number;
  forwardX: number;
  forwardY: number;
  forwardZ: number;
  tilt?: number;
  displayName?: string;
  isLive?: boolean;
  fovMax?: number;
  fovMin?: number;
  distMax?: number;
  distMin?: number;
  offset?: number;
}

export const useCreateGameCamerasMutation = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ 
      gameId, 
      data 
    }: { 
      gameId: string | number; 
      data: CreateGameCamerasFromSetupData | CreateSingleGameCameraData;
    }) => {
      const response = await post<GameCamera | GameCamera[]>(`/v1.0/games/${gameId}/cameras`, data);
      return { gameId, data: response.data };
    },
    onSuccess: ({ gameId }) => {
      // Invalidate game cameras query
      queryClient.invalidateQueries({ 
        queryKey: getGameCamerasQueryKey(gameId) 
      });
      
      const isSetupCreation = 'cameraSetupId' in arguments[0];
      const message = isSetupCreation 
        ? 'Game cameras created from setup successfully' 
        : 'Game camera created successfully';
      
      enqueueSnackbar(message, { variant: 'success' });
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.message || 'Failed to create game cameras';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
};

export default useCreateGameCamerasMutation;